<template>
  <div class="h-100 d-flex flex-column">
    <div class="row f-row h-100 justify-content-center">
      <div
        class="col col-12 text-center justify-content-center"
        style="width:100%; padding: 0 5px 0 5px; height: calc(100vh - 126px)!important;"
      >
        <DataTable
          :alwaysShowPaginator="true"
          :filters.sync="filters"
          :filterDisplay="showFilters ? 'menu' : ''"
          class="p-datatable-sm p-datatable-expo-hedges"
          columnResizeMode="expand"
          currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
          :first.sync="tableFirst"
          :key="tableKey"
          :loading="managementLoading"
          :paginator="true"
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          ref="dt"
          resizableColumns
          responsiveLayout="scroll"
          :rowClass="rowStyle"
          :rowHover="true"
          :rows.sync="tableRows"
          :rowsPerPageOptions="[10, 15, 20, 25, 30, 50, 100, 200, 500, 1000]"
          :scrollable="true"
          stickyHeader="true"
          style="table-layout: fixed;"
          :value="filteredTableData"
          :virtual="true"
        >
          <template #empty>
            <div class="col col-auto">
              <span class="warning-pill" style="padding: 0 5px 0 5px">{{
                noDataLegend
              }}</span>
            </div>
          </template>
          <template #loading>
            Loading data. Please wait.
          </template>

          <Column key="toggle" :styles="{ 'max-width': '4rem' }">
            <template #header>
              <span
                v-if="
                  filteredTableData.some((row) => row.total || row.sub_total)
                "
                class="toggle-arrow"
                @click="toggleAllRows"
                style="cursor: pointer;"
              >
                <i
                  :class="
                    allCollapsed && isAllRowsCollapsed
                      ? 'fa fa-chevron-right'
                      : 'fa fa-chevron-down'
                  "
                  aria-hidden="true"
                ></i>
              </span>
            </template>
            <template #body="slotProps">
              <span
                v-if="slotProps.data.total || slotProps.data.sub_total"
                class="toggle-arrow"
                @click="toggleRow(slotProps.data)"
                style="cursor: pointer;"
              >
                <i
                  :class="getArrowClass(slotProps.data)"
                  aria-hidden="true"
                ></i>
              </span>
            </template>
          </Column>

          <Column
            v-for="col in selectedColumns"
            :field="col.field"
            :header="col.header"
            :key="col.id"
            :styles="col.styles"
          >
            <template #body="slotProps">
              <template>
                <span
                  v-if="
                    dontShowTotal.includes(col.field) && slotProps.data['total']
                  "
                >
                </span>
                <span
                  v-else-if="
                    dontShowSubTotal.includes(col.field) &&
                      slotProps.data['sub_total']
                  "
                >
                </span>
                <span
                  v-else-if="
                    dontShowPivot.includes(col.field) &&
                      !slotProps.data['sub_total'] &&
                      !slotProps.data['total']
                  "
                >
                </span>
                <span
                  v-else-if="col.dataType === 'date'"
                  :class="{
                    'center-pill': col.field === 'trade_date',
                    'value-pill':
                      (slotProps.data[col.field] >= today) &
                      (col.field !== 'trade_date'),
                    'ok-pill':
                      (slotProps.data[col.field] < today) &
                      (col.field !== 'trade_date'),
                  }"
                  >{{ slotProps.data[col.field] | dateFormat }}
                </span>
                <span v-else-if="col.dataType === 'futDate'">{{
                  slotProps.data[col.field] | futuresDate
                }}</span>
                <span
                  v-else-if="col.dataType === 'number'"
                  class="right-pill"
                  :class="{
                    'value-pill':
                      valuePill.includes(col.field) &&
                      (slotProps.data['total'] || slotProps.data['sub_total']),
                  }"
                  >{{ slotProps.data[col.field] | numberFormat }}</span
                >
                <span
                  v-else-if="col.dataType === 'price'"
                  class="right-pill"
                  :class="{ 'value-pill': valuePill.includes(col.field) }"
                  >{{ slotProps.data[col.field] | prFormat }}</span
                >
                <span
                  v-else-if="col.dataType === 'priceNone'"
                  class="right-pill"
                  :class="{ 'value-pill': valuePill.includes(col.field) }"
                  >{{ slotProps.data[col.field] | prNoneFormat }}</span
                >
                <span
                  v-else
                  :class="{
                    'right-pill': col.field === 'q_min_max',
                    'ok-pill':
                      expiredPill.includes(col.field) &&
                      slotProps.data[col.field] === 'Fixed',
                    'warning-pill':
                      expiredPill.includes(col.field) &&
                      (slotProps.data[col.field] === 'Unhedged' ||
                        slotProps.data[col.field] === 'Expired'),
                    'expired-pill':
                      expiredPill.includes(col.field) &&
                      (slotProps.data[col.field] === 'Speculate' ||
                        slotProps.data[col.field] === 'Warning'),
                    'value-pill':
                      openPill.includes(col.field) &&
                      slotProps.data[col.field] === 'Open',
                  }"
                  >{{ slotProps.data[col.field] }}</span
                >
              </template>
            </template>

            <!-- Filtros condicionales -->
            <template
              #filter="{ filterModel }"
              v-if="showFilters.includes(col.field)"
            >
              <template v-if="col.field === 'client_str'">
                <InputText
                  type="text"
                  v-model="filterModel.value"
                  class="p-column-filter"
                  :placeholder="`Search by ${col.header.toLowerCase()}`"
                />
              </template>

              <template v-else-if="col.field === 'contract_month_year'">
                <div class="mb-3 font-bold">Contract Month Year Picker</div>
                <MultiSelect
                  v-model="filterModel.value"
                  :options="getUniqueValues(col.field)"
                  :placeholder="`Select ${col.header}`"
                  class="p-column-filter"
                  scrollHeight="400px"
                >
                  <template #option="slotProps">
                    <div class="p-multiselect-representative-option">
                      <span class="image-text">{{ slotProps.option }}</span>
                    </div>
                  </template>
                </MultiSelect>
              </template>

              <template v-else-if="col.field === 'end_product_str'">
                <div class="mb-3 font-bold">End Product Picker</div>
                <MultiSelect
                  v-model="filterModel.value"
                  :options="getUniqueValues(col.field)"
                  :placeholder="`Select ${col.header}`"
                  class="p-column-filter"
                  scrollHeight="400px"
                >
                  <template #option="slotProps">
                    <div class="p-multiselect-representative-option">
                      <span class="image-text">{{ slotProps.option }}</span>
                    </div>
                  </template>
                </MultiSelect>
              </template>

              <template
                v-else-if="col.field === 'date' || col.dataType === 'date'"
              >
                <Calendar
                  v-model="filterModel.value"
                  dateFormat="mm/dd/yy"
                  :placeholder="`Select ${col.header}`"
                  class="p-column-filter"
                />
              </template>

              <template v-else-if="col.field === 'status'">
                <div class="mb-3 font-bold">Status Picker</div>
                <MultiSelect
                  v-model="filterModel.value"
                  :options="getUniqueValues(col.field)"
                  :placeholder="`Select ${col.header}`"
                  class="p-column-filter"
                  scrollHeight="400px"
                >
                  <template #option="slotProps">
                    <div class="p-multiselect-representative-option">
                      <span class="image-text">{{ slotProps.option }}</span>
                    </div>
                  </template>
                </MultiSelect>
              </template>

              <template v-else>
                <InputText
                  type="text"
                  v-model="filterModel.value"
                  class="p-column-filter"
                  :placeholder="`Search by ${col.header.toLowerCase()}`"
                />
              </template>
            </template>
          </Column>
        </DataTable>
      </div>
    </div>
  </div>
</template>

<script>
import Calendar from "primevue/calendar";
import Column from "primevue/column";
import DataTable from "primevue/datatable";
import InputText from "primevue/inputtext";
import MultiSelect from "primevue/multiselect";
import { defaultAnalytics } from "@/_core/data/Analytics/AnalyticsHelpers.js";
import { format, timeFormat } from "d3";
import { FilterMatchMode, FilterOperator } from "primevue/api";

export default {
  name: "ManagementTable",
  components: {
    Column,
    Calendar,
    InputText,
    MultiSelect,
    DataTable,
  },
  props: {
    Analytics: {
      required: true,
      default: defaultAnalytics(),
    },
    managementLoading: { required: true, type: Boolean, default: false },
    managementSelectedCol: { required: false, default: () => [] },
    selectedTable: { required: true, default: "strategies" },
    selectedUnit: { required: true, default: "contracts" },
  },
  data() {
    return {
      availableColumns: this.Analytics.managementFields,
      expiredPill: ["status"],
      initialDate: new Date(new Date().getFullYear() - 1, 0, 1),
      calculating: false,
      defaultColumns: [],
      dontShowPivot: [],
      dontShowSubTotal: [],
      dontShowTotal: [],
      noDataLegend: "No available data.",
      openPill: ["status"],
      parsedOriginalData: [],
      selectedColumns: [],
      selectedHeaders: [],
      tableData: [],
      tableFirst: 0,
      tableKey: 0,
      tableRows: 25,
      today: new Date(),
      valuePill: ["hedge_value"],
      collapseState: {}, // Tracks collapse state for total and sub-total rows
      expandedRows: {}, // Similar to CommodityStrategyTable
      showFilters: [
        "client_str",
        "end_product_str",
        "contract_month_year",
        "date",
        "status",
      ],
      filters: {
        client_str: {
          operator: FilterOperator.AND,
          constraints: [
            { value: null, matchMode: FilterMatchMode.STARTS_WITH },
          ],
        },
        end_product_str: { value: null, matchMode: FilterMatchMode.IN },
        contract_month_year: { value: null, matchMode: FilterMatchMode.IN },
        date: {
          operator: FilterOperator.AND,
          constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }],
        },
        status: { value: null, matchMode: FilterMatchMode.IN },
      },
      allCollapsed: true,
    };
  },
  computed: {
    filteredTableData() {
      return this.tableData.filter((row) => {
        // Always show total rows
        if (row.total) return true;

        // For sub-total rows, check if parent total is expanded
        if (row.sub_total) {
          const parentKey = this.getParentKey(row);
          return parentKey ? !this.isRowCollapsed(parentKey) : true;
        }

        // For regular rows, check both parent total and sub-total
        const parentKey = this.getParentKey(row);
        const subParentKey = this.getSubParentKey(row);

        const parentCollapsed = parentKey
          ? this.isRowCollapsed(parentKey)
          : false;
        const subParentCollapsed = subParentKey
          ? this.isRowCollapsed(subParentKey)
          : false;

        return !parentCollapsed && !subParentCollapsed;
      });
    },
    isAllRowsCollapsed() {
      return Object.values(this.collapseState).every((collapsed) => collapsed);
    },
  },
  watch: {
    managementSelectedCol: {
      handler(newVal) {
        this.selectedColumns = this.defaultColumns
          .filter((col) => newVal.includes(col))
          .map((field) =>
            [...this.availableColumns].find((col) => col.field === field),
          )
          .filter((col) => col !== undefined);
        this.tableKey += 1;
      },
      deep: true,
    },
    "Analytics.management": {
      handler() {
        this.createTableData();
        this.tableDataChanged();
      },
      deep: true,
    },
    selectedUnit() {
      let tableData = this.Analytics["management"][this.selectedTable];
      tableData = this.editTableData(tableData);
      Object.assign(this, {
        tableData: tableData,
      });
      this.tableKey += 1;
    },
    selectedTable() {
      this.getTableSetup();
      this.createTableData();
      this.tableDataChanged();
    },
    visibleColumns() {
      this.tableKey += 1;
    },
    collapseState: {
      handler() {
        if (this.isAllRowsCollapsed) {
          this.allCollapsed = true;
        } else {
          this.allCollapsed = false;
        }
      },
      deep: true,
    },
    tableData() {
      this.$emit("eventTableData", this.tableData);
    },
  },
  created() {},
  mounted() {
    this.getTableSetup();
    this.createTableData();
    this.tableDataChanged();
  },
  methods: {
    tableDataChanged() {
      let selectedHeaders = [...this.defaultColumns];
      if (this.tableData.length > 0) {
        let includeBool = true;
        if (selectedHeaders.includes("end_product_str")) {
          includeBool = this.showEndProduct();
          if (!includeBool) {
            selectedHeaders.splice(
              selectedHeaders.indexOf("end_product_str"),
              1,
            );
          }
        }
        if (selectedHeaders.includes("balance")) {
          includeBool = this.showBalance();
          if (!includeBool) {
            selectedHeaders.splice(selectedHeaders.indexOf("balance"), 1);
          }
        }
        if (selectedHeaders.includes("ratio")) {
          includeBool = this.showRatio();
          if (!includeBool) {
            selectedHeaders.splice(selectedHeaders.indexOf("ratio"), 1);
          }
        }
        if (selectedHeaders.includes("base")) {
          includeBool = this.showBasis();
          if (!includeBool) {
            selectedHeaders.splice(selectedHeaders.indexOf("base"), 1);
          }
        }
        if (selectedHeaders.includes("local_costs")) {
          includeBool = this.showFX();
          if (!includeBool) {
            selectedHeaders.splice(selectedHeaders.indexOf("local_costs"), 1);
            selectedHeaders.splice(selectedHeaders.indexOf("m2m_fx"), 1);
          }
        }
      }
      this.$emit("eventSelectedColumns", selectedHeaders);
    },
    showEndProduct() {
      return (
        new Set(
          this.tableData
            .map((row) => row.end_product_str)
            .filter((val) => val !== null),
        ).size > 1
      );
    },
    showBalance() {
      return !this.tableData.every(
        (item) => item.balance === 0 || item.balance == null,
      );
    },
    showRatio() {
      return this.tableData.some(
        (item) => item.ratio !== 1 && item.ratio !== null,
      );
    },
    showBasis() {
      return this.tableData.some(
        (item) => item.base !== 0 && item.base !== null,
      );
    },
    showFX() {
      return this.tableData.some(
        (item) => item.local_costs !== 0 && item.local_costs !== null,
      );
    },
    getUniqueValues(field) {
      if (!this.filteredTableData || this.filteredTableData.length === 0) {
        return [];
      }

      const uniqueValues = [
        ...new Set(
          this.filteredTableData
            .map((item) => item[field])
            .filter(
              (value) => value !== null && value !== undefined && value !== "",
            ),
        ),
      ];

      return uniqueValues.sort();
    },
    editTableData(tableData) {
      let selectedUnit = this.selectedUnit;
      let expoUnitChange, hedgeUnitChange;

      let changeUnit = function(value, unitChange) {
        return value != null ? unitChange * value : null;
      };

      tableData.forEach(function(d) {
        expoUnitChange =
          selectedUnit === "contracts"
            ? (1 * d.unit_change * d.ratio) / d.commodity_size
            : selectedUnit === "product"
            ? 1
            : selectedUnit === "futures"
            ? 1 * d.unit_change * d.ratio
            : NaN;
        hedgeUnitChange =
          selectedUnit === "contracts"
            ? 1 / d.commodity_size
            : selectedUnit === "product"
            ? 1 / d.unit_change / d.ratio
            : selectedUnit === "futures"
            ? 1
            : NaN;
        // Change expo units
        d.cash_flow_ch = changeUnit(d.cash_flow, expoUnitChange);
        d.balance_ch = changeUnit(d.balance, expoUnitChange);
        // Change hedge units
        d.hedgeNotional_ch = changeUnit(d.hedgeNotional, hedgeUnitChange);
        d.q_current_ch = changeUnit(d.q_current, hedgeUnitChange);
        d.q_min_ch = changeUnit(+d.q_min, hedgeUnitChange);
        d.q_max_ch = changeUnit(+d.q_max, hedgeUnitChange);
        d.q_min_max =
          format(",.3r")(Math.round(+d.q_min_ch * 10000) / 10000) +
          " | " +
          format(",.3r")(Math.round(+d.q_max_ch * 10000) / 10000);
        d.delta_ch = changeUnit(+d.delta, hedgeUnitChange);
        d.gamma_ch = changeUnit(+d.gamma, hedgeUnitChange);
        d.theta_ch = changeUnit(+d.theta, hedgeUnitChange);
        d.vega_ch = changeUnit(+d.vega, hedgeUnitChange);
        d.rho_ch = changeUnit(+d.rho, hedgeUnitChange);
      });
      return tableData;
    },
    createTableData() {
      let tableData = this.Analytics["management"][this.selectedTable];
      if (tableData.length === 0) {
        Object.assign(this, {
          tableData: [],
          collapseState: {},
          expandedRows: {},
        });
        return;
      }

      // Improved grouping logic
      let currentGroupId = null;
      let currentSubGroupId = null;
      let groupCounter = 0;
      let subGroupCounter = 0;

      tableData.forEach((d) => {
        if (d.total) {
          currentGroupId = `total_${groupCounter++}`;
          d.groupId = currentGroupId;
          d.subGroupId = null;
        } else if (d.sub_total) {
          currentSubGroupId = `subtotal_${subGroupCounter++}`;
          d.groupId = currentGroupId;
          d.subGroupId = currentSubGroupId;
        } else {
          // Regular row
          d.groupId = currentGroupId;
          d.subGroupId = currentSubGroupId;
        }
      });

      tableData = this.editTableData(tableData);

      // Initialize collapse state - all rows start expanded
      const collapseState = {};
      const expandedRows = {};

      tableData.forEach((row, index) => {
        if (row.total) {
          collapseState[row.groupId] = true; // false = expanded
          expandedRows[index] = false; // true = expanded
        }
        if (row.sub_total) {
          collapseState[row.subGroupId] = true; // false = expanded
          expandedRows[index] = false; // true = expanded
        }
      });

      Object.assign(this, {
        tableData: tableData,
        collapseState: collapseState,
        expandedRows: expandedRows,
      });
      this.tableKey += 1;
    },

    getParentKey(row) {
      if (row.total) return null;
      return row.groupId;
    },

    getSubParentKey(row) {
      if (row.total || row.sub_total) return null;
      return row.subGroupId;
    },

    isRowCollapsed(key) {
      return key ? this.collapseState[key] === true : false;
    },

    toggleRow(row) {
      let key;
      let index = this.tableData.findIndex((item) => item === row);

      if (row.total) {
        key = row.groupId;
      } else if (row.sub_total) {
        key = row.subGroupId;
      }

      if (key) {
        // Toggle collapse state
        this.$set(this.collapseState, key, !this.collapseState[key]);

        // Update expanded rows state (similar to CommodityStrategyTable)
        this.$set(this.expandedRows, index, !this.expandedRows[index]);

        // // Force table refresh
        // this.tableKey += 1;
      }
    },

    toggleAllRows() {
      this.allCollapsed = !this.isAllRowsCollapsed;

      for (let key in this.collapseState) {
        this.$set(this.collapseState, key, this.allCollapsed);
      }

      // this.tableKey += 1;
    },

    getArrowClass(row) {
      if (row.total || row.sub_total) {
        const key = row.total ? row.groupId : row.subGroupId;
        const isCollapsed = this.isRowCollapsed(key);
        return isCollapsed ? "fa fa-chevron-right" : "fa fa-chevron-down";
      }
      return "";
    },

    getTableSetup() {
      let selectedHeaders = [];
      switch (this.selectedTable) {
        case "strategies":
          this.dontShowTotal = ["counterparty", "contract_month_year_hedge"];
          this.dontShowSubTotal = [
            "custom_id",
            "end_product_str",
            "budget_futures",
            "cash_flow_ch",
            "balance_ch",
            "contract_month_year",
            "date",
            "fixation_date",
            "base",
            "ratio",
          ];
          this.dontShowPivot = [
            "custom_id",
            "end_product_str",
            "budget_futures",
            "cash_flow_ch",
            "balance_ch",
            "contract_month_year",
            "date",
            "fixation_date",
            "base",
            "ratio",
            "contract_month_year_hedge",
            "counterparty",
            "strategy_name",
          ];
          selectedHeaders = [
            "contract_month_year",
            "custom_id",
            "fixation_date",
            "cash_flow_ch",
            "balance_ch",
            "budget_futures",
            "ratio",
            "base",
            "status",
            "contract_month_year_hedge",
            "counterparty",
            "strategy_name",
            "weighted_strikes",
            "fixing_date",
            "hedgeNotional_ch",
            "q_min_max",
            "q_current_ch",
            "delta_ch",
            "spot",
            "strategy_strike",
            "hedge_value",
          ];
          break;
        case "date_2":
          this.dontShowTotal = ["counterparty", "contract_month_year_hedge"];
          this.dontShowSubTotal = [
            "monthDate",
            "custom_id",
            "contract_month_year",
            "fixation_date",
            "cash_flow_ch",
            "balance_ch",
            "budget_futures",
            "base",
            "ratio",
          ];
          this.dontShowPivot = [
            "monthDate",
            "custom_id",
            "contract_month_year",
            "fixation_date",
            "cash_flow_ch",
            "balance_ch",
            "budget_futures",
            "base",
            "ratio",
            "contract_month_year_hedge",
            "counterparty",
            "strategy_name",
          ];
          selectedHeaders = [
            "monthDate",
            "custom_id",
            "contract_month_year",
            "fixation_date",
            "cash_flow_ch",
            "balance_ch",
            "budget_futures",
            "ratio",
            "base",
            "status",
            "contract_month_year_hedge",
            "counterparty",
            "strategy_name",
            "weighted_strikes",
            "fixing_date",
            "hedgeNotional_ch",
            "q_min_max",
            "q_current_ch",
            "delta_ch",
            "gamma_ch",
            "theta_ch",
            "vega_ch",
            "rho_ch",
            "spot",
            "strategy_strike",
            "hedge_value",
          ];
          break;
        case "contracts":
          this.dontShowTotal = ["counterparty"];
          this.dontShowSubTotal = [
            "custom_id",
            "contract_month_year",
            "fixation_date",
            "balance_ch",
            "budget_futures",
            "ratio",
            "base",
          ];
          this.dontShowPivot = [
            "custom_id",
            "contract_month_year",
            "fixation_date",
            "balance_ch",
            "budget_futures",
            "ratio",
            "base",
            "contract_month_year_hedge",
            "counterparty",
            "strategy_name",
          ];
          selectedHeaders = [
            "custom_id",
            "contract_month_year",
            "fixation_date",
            "balance_ch",
            "budget_futures",
            "ratio",
            "base",
            "status",
            "contract_month_year_hedge",
            "counterparty",
            "strategy_name",
            "weighted_strikes",
            "fixing_date",
            "hedgeNotional_ch",
            "q_min_max",
            "q_current_ch",
            "delta_ch",
            "spot",
            "strategy_strike",
            "hedge_value",
          ];
          break;
        case "clients":
          this.dontShowTotal = [
            "end_product_str",
            "balance_ch",
            "contract_month_year",
            "base",
            "ratio",
            "monthDate",
          ];
          this.dontShowSubTotal = [
            "client_str",
            "contract_month_year_hedge",
            "strategy_name",
            "fixing_date",
            "weighted_strikes",
          ];
          this.dontShowPivot = [
            "end_product_str",
            "client_str",
            "balance_ch",
            "commodity",
            "contract_month_year",
            "monthDate",
            "fixation_date",
            "base",
            "ratio",
          ];
          selectedHeaders = [
            "client_str",
            "end_product_str",
            "balance_ch",
            "cash_flow_ch",
            "commodity",
            "contract_month_year",
            "monthDate",
            "fixation_date",
            "status",
            "base",
            "ratio",
            "commodity_hedge",
            "contract_month_year_hedge",
            "strategy_name",
            "fixing_date",
            "weighted_strikes",
            "hedgeNotional_ch",
            "q_min_max",
            "q_current_ch",
            "delta_ch",
            "spot",
            "strategy_strike",
            "local_costs",
            "hedge_value",
            "m2m_fx",
          ];
          break;
        case "suppliers":
          this.dontShowTotal = [
            "end_product_str",
            "balance_ch",
            "contract_month_year",
            "base",
            "ratio",
            "monthDate",
            "fixation_date",
          ];
          this.dontShowSubTotal = [
            "supplier",
            "contract_month_year_hedge",
            "strategy_name",
            "fixing_date",
            "weighted_strikes",
          ];
          this.dontShowPivot = [
            "end_product_str",
            "supplier",
            "balance_ch",
            "contract_month_year",
            "monthDate",
            "fixation_date",
            "base",
            "ratio",
          ];
          selectedHeaders = [
            "supplier",
            "end_product_str",
            "balance_ch",
            "contract_month_year",
            "monthDate",
            "fixation_date",
            "status",
            "base",
            "ratio",
            "commodity",
            "contract_month_year_hedge",
            "strategy_name",
            "fixing_date",
            "weighted_strikes",
            "hedgeNotional_ch",
            "q_min_max",
            "q_current_ch",
            "delta_ch",
            "spot",
            "strategy_strike",
            "hedge_value",
          ];
          break;
        default:
          console.error("Table not recognized");
      }
      this.defaultColumns = [...selectedHeaders];
      let availableHeaders = [...selectedHeaders];
      this.$emit("eventAvailableColumns", availableHeaders);
      this.$emit("eventSelectedColumns", selectedHeaders);
    },
    rowStyle(data) {
      if (data.total) return "total-row";
      if (data.sub_total) return "sub-total-row";
      return "";
    },
  },
  filters: {
    dateFormat: function(val) {
      if (val === null || val === "" || val === 0 || val === undefined) {
        return "";
      }
      return +val < +new Date(1980, 1, 1) ? "" : timeFormat("%d %b %y")(val);
    },
    prFormat: function(val) {
      return val === null || val === "" || val === 0 || val === undefined
        ? 0
        : Math.abs(val) < 0.000001
        ? 0
        : format(",.5r")(val);
    },
    prNoneFormat: function(val) {
      return val === null || val === "" || val === 0 || val === undefined
        ? "-"
        : format(",.5r")(val);
    },
    numberFormat: function(val) {
      if (val === null || val === "" || val === undefined) {
        return "";
      }
      if (Math.abs(val) < 0.000001) {
        return "-";
      }
      if (Math.abs(val) < 1) {
        return Number(val.toFixed(3)).toString();
      }
      return format(",.4r")(val);
    },
    futuresDate: function(val) {
      if (val === null || val === "" || val === 0 || val === undefined) {
        return "";
      }
      return +val < +new Date(1980, 1, 1)
        ? ""
        : timeFormat("%b%y")(val).toUpperCase();
    },
  },
};
</script>

<style>
.total-row {
  background-color: var(--lightbackgroundTable) !important;
}

.sub-total-row {
  background-color: var(--lightbackground3Table) !important;
}

.sub-total-row td:first-child span {
  margin-left: 25px;
}

.p-datatable-expo-hedges > .p-datatable-wrapper > table > tbody > tr > td {
  border-bottom: 1px solid var(--backgroundTable);
}

.p-datatable-expo-hedges > .p-datatable-wrapper {
  height: calc(100vh - 180px) !important;
  overflow-y: auto;
}

.p-datatable-expo-hedges .p-datatable-table {
  table-layout: auto !important;
  width: auto !important;
}

.p-datatable-expo-hedges .p-datatable-thead > tr > th,
.p-datatable-expo-hedges .p-datatable-tbody > tr > td {
  white-space: nowrap !important;
}

.toggle-arrow {
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1rem;
  height: 20px;
  color: var(--textactive);
  font-size: 14px;
  transition: all 0.3s ease;
}

.toggle-arrow:hover {
  color: var(--primary);
}

.toggle-arrow i {
  font-size: 14px;
}
</style>
